import * as React from "react"

/**
 * Factory for Central icon system glyphs (DESIGN.md "Icons").
 *
 * Every icon is a 24×24 viewBox with the 1.5 px stroke already outlined into
 * fill paths (variant stroke 1.5 / medium corners / round joins), so colour
 * follows `currentColor` and `strokeWidth` is accepted only for API
 * compatibility with the previous lucide components.
 */
export type IconProps = React.SVGProps<SVGSVGElement> & {
  size?: number | string
  strokeWidth?: number | string
  absoluteStrokeWidth?: boolean
}

export type IconComponent = React.ForwardRefExoticComponent<
  IconProps & React.RefAttributes<SVGSVGElement>
>

type PathTuple = [fillRule: "nonzero" | "evenodd", d: string]

export function createCentralIcon(slug: string, paths: PathTuple[]): IconComponent {
  const Icon = React.forwardRef<SVGSVGElement, IconProps>(
    (
      { size = 24, strokeWidth: _strokeWidth, absoluteStrokeWidth: _abs, className, ...rest },
      ref
    ) => (
      <svg
        ref={ref}
        aria-hidden={rest["aria-label"] ? undefined : true}
        className={["central-icon", `central-${slug}`, className].filter(Boolean).join(" ")}
        fill="currentColor"
        height={size}
        viewBox="0 0 24 24"
        width={size}
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
      >
        {paths.map(([fillRule, d], i) => (
          <path key={i} d={d} fillRule={fillRule} />
        ))}
      </svg>
    )
  )
  Icon.displayName = `CentralIcon(${slug})`
  return Icon
}
